import { useEffect, useMemo, useRef } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { SFormikTextfield } from '@setvi/shared/components';
import { useCustomErrors } from '@setvi/shared/hooks';
import { linkAddress } from '@setvi/shared/utils';

import { makeStyles } from '@material-ui/core';
import {
  InputContainer,
  FormikContainer,
  Placeholder,
  inputStyle
} from '../styled';
import { SearchFormProps } from '..';
import { ClearButton } from '../components';
import { formId } from '../text';
import { SearchFormOptions } from '../../../enums';
import { ProductMatchValues } from '../../..';

const useStyles = makeStyles(() => ({
  root: {
    // '& .MuiOutlinedInput-root': {
    //   borderRadius: '12px !important', // Add border-radius of 12px
    //   '& fieldset': {
    //     borderColor: 'gray' // Default border color (gray)
    //   },
    //   '&:hover fieldset': {
    //     borderColor: 'gray' // Border color on hover (optional)
    //   },
    //   '&.Mui-focused fieldset': {
    //     borderColor: 'transparent', // Remove default blue border on focus
    //     borderWidth: 2, // Border width for focused state
    //     borderImage: 'linear-gradient(45deg, #ff007f, #ff7300) 1' // Apply gradient on focus
    //   },
    //   '&.Mui-error fieldset': {
    //     borderColor: 'red' // Border color when input is invalid
    //   }
    // }
  }
}));

export enum VALIDATORS {
  URL = 2048
}

export const urlValidation = Yup.object({
  [ProductMatchValues.Url]: Yup.string()
    .max(VALIDATORS.URL, `Limit is ${VALIDATORS.URL} characters`)
    .matches(linkAddress, 'Enter correct URL!')
});

export const schema = [
  {
    name: ProductMatchValues.Url,
    schema: urlValidation
  }
];

const MIN_ROWS = 4;
interface WebUrlInputProps extends Omit<
  SearchFormProps,
  'disableButton' | 'imgTextRows' | 'textRows'
> {
  rows?: number;
  handleInputValidation: (invalid: boolean) => void;
}

const WebUrlInput = ({
  values,
  rows = MIN_ROWS,
  handleInputValidation,
  onSubmit,
  onChange
}: WebUrlInputProps) => {
  const classes = useStyles();
  const { customErrors, customValidate } = useCustomErrors({
    schemas: schema,
    values: { [ProductMatchValues.Url]: values?.[ProductMatchValues.Url] }
  });
  const ref = useRef(null);
  // @ts-ignore
  const isValid = !customErrors?.url && !!values?.[ProductMatchValues.Url];

  const initialValues = useMemo(
    () => ({
      [ProductMatchValues.Url]: values?.[ProductMatchValues.Url]
    }),
    [values]
  );

  useEffect(() => {
    handleInputValidation?.(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customErrors]);

  const handleFocus = async () => {
    const textarea = ref?.current?.querySelector('textarea');
    if (textarea.value !== values?.[ProductMatchValues.Url]) {
      textarea.value = values?.[ProductMatchValues.Url];
    }

    await textarea?.focus();
    // Move cursor to the end
    const length = textarea?.value?.length || 0;
    textarea?.setSelectionRange(length, length);
  };

  useEffect(() => {
    handleFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormikContainer>
      <Formik
        onSubmit={() =>
          isValid &&
          onSubmit?.(SearchFormOptions.url, {
            value: values?.[ProductMatchValues.Url],
            [ProductMatchValues.Url]: values?.[ProductMatchValues.Url]
          })
        }
        validateOnChange={false}
        enableReinitialize
        initialValues={initialValues}>
        {({ values: formikValues, resetForm, submitForm }) => (
          <Form id={formId}>
            <InputContainer
              // @ts-ignore
              ref={ref}>
              {!formikValues?.[ProductMatchValues.Url] && (
                <Placeholder>
                  <p>
                    Copy and paste or enter the URL for the product link. Ex.
                    https://www.homedepot.com/p/ZEP-1-Gal-Neutral-Floor-Cleaner-ZUNEUT128/202204696
                  </p>
                </Placeholder>
              )}
              <SFormikTextfield
                name={SearchFormOptions.url}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    submitForm();
                  }
                }}
                placeholder=""
                maxLength={VALIDATORS.URL}
                onChange={(value: string) => {
                  customValidate({
                    schema: urlValidation,
                    value,
                    name: SearchFormOptions.url
                  });
                  onChange({ [ProductMatchValues.Url]: value });
                }}
                multiline
                minRows={rows}
                maxRows={rows}
                inputClass={classes.root}
                InputProps={{
                  style: inputStyle,
                  endAdornment: !!formikValues?.url && (
                    <ClearButton
                      onClear={() => {
                        resetForm();
                        onChange({ [ProductMatchValues.Url]: '' });
                      }}
                    />
                  )
                }}
                customErrors={customErrors}
              />
            </InputContainer>
          </Form>
        )}
      </Formik>
    </FormikContainer>
  );
};

export default WebUrlInput;
